/**
 * DNEEZ — Luxury Product Page JS
 * Handles: variant switching, qty, ATC, accordions,
 *          mobile gallery dots, wishlist, review modal,
 *          size-guide modal, star picker, review form
 *
 * CRITICAL: Never rename Prestige classes used by theme.min.js:
 *   .ProductForm__AddToCart, [data-product-json], .ProductSelect,
 *   .QuantitySelector__CurrentQuantity, .ProductMeta__Price
 */

(function () {
  'use strict';

  /* ══════════════════════════════════════════════════════
     INIT — wait for DOM
     ══════════════════════════════════════════════════════ */
  document.addEventListener('DOMContentLoaded', function () {
    DNProduct.init();
  });

  var DNProduct = {

    /* ── State ─────────────────────────────────────────── */
    variants: [],
    selectedOptions: [],
    currentVariant: null,

    init: function () {
      var productJson = document.getElementById('dneez-product-json');
      if (!productJson) return;

      try {
        var data = JSON.parse(productJson.textContent);
        this.variants = data.product.variants || [];
        this.currentVariant = data.product.variants.find(function (v) {
          return v.id === data.selected_variant_id;
        }) || data.product.variants[0];
      } catch (e) {
        console.warn('DNProduct: JSON parse error', e);
        return;
      }

      // Read initial selected options from current variant
      if (this.currentVariant) {
        this.selectedOptions = [
          this.currentVariant.option1,
          this.currentVariant.option2,
          this.currentVariant.option3
        ];
      }

      this.bindOptionButtons();
      this.bindQuantity();
      this.bindWishlist();
      this.bindAccordions();
      this.bindMobileGallery();
      this.bindModals();
      this.bindReviewForm();
      this.bindStarPicker();
      this.renderReviews();
    },

    /* ══════════════════════════════════════════════════════
       OPTION BUTTONS  (size + colour)
       ══════════════════════════════════════════════════════ */
    bindOptionButtons: function () {
      var self = this;
      var btns = document.querySelectorAll('.dneez-size-btn, .dneez-swatch');

      btns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.classList.contains('is-unavailable')) return;

          var optionIndex = parseInt(btn.dataset.optionIndex, 10);
          var value = btn.dataset.value;

          // Update visual selection in this group
          var group = btn.closest('.dneez-form__option-group');
          if (group) {
            group.querySelectorAll('.dneez-size-btn, .dneez-swatch').forEach(function (b) {
              b.classList.remove('is-selected');
            });
          }
          btn.classList.add('is-selected');

          // Update selected options array
          self.selectedOptions[optionIndex] = value;

          // Update displayed label
          var label = document.getElementById('dneez-option-val-' + optionIndex);
          if (label) label.textContent = value;

          // Find matching variant
          self.updateVariant();
        });
      });
    },

    updateVariant: function () {
      var self = this;
      var matched = this.variants.find(function (v) {
        return (
          (self.selectedOptions[0] == null || v.option1 === self.selectedOptions[0]) &&
          (self.selectedOptions[1] == null || v.option2 === self.selectedOptions[1]) &&
          (self.selectedOptions[2] == null || v.option3 === self.selectedOptions[2])
        );
      });

      if (!matched) return;
      this.currentVariant = matched;
      this.updateUI(matched);
      this.updateURL(matched);
    },

    updateUI: function (variant) {
      // Price
      var priceEl = document.getElementById('dneez-price');
      if (priceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          var savePct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
          priceEl.innerHTML =
            '<span class="dneez-product__price-sale">' + this.formatMoney(variant.price) + '</span>' +
            '<span class="dneez-product__price-compare">' + this.formatMoney(variant.compare_at_price) + '</span>' +
            '<span class="dneez-product__price-badge">-' + savePct + '%</span>';
        } else {
          priceEl.innerHTML =
            '<span class="dneez-product__price-current">' + this.formatMoney(variant.price) + '</span>';
        }
      }

      // ATC button price & label
      var atcPrice = document.getElementById('dneez-atc-price');
      if (atcPrice) atcPrice.textContent = this.formatMoney(variant.price);

      // ATC button state
      var atcBtn = document.getElementById('dneez-add-to-cart');
      var atcLabel = document.getElementById('dneez-atc-label');
      if (atcBtn) {
        if (variant.available) {
          atcBtn.disabled = false;
          atcBtn.classList.remove('dneez-atc-btn--soldout');
          if (atcLabel) atcLabel.textContent = 'Add to Cart';
        } else {
          atcBtn.disabled = true;
          atcBtn.classList.add('dneez-atc-btn--soldout');
          if (atcLabel) atcLabel.textContent = 'Sold Out';
        }
      }

      // Hidden variant select — CRITICAL for Prestige JS
      var select = document.querySelector('.ProductSelect');
      if (select) select.value = variant.id;

      // Hidden input inside form
      var hiddenInput = document.querySelector('#dneez-product-form input[name="id"]');
      if (hiddenInput) hiddenInput.value = variant.id;

      // Wishlist variant
      var wishlistBtn = document.getElementById('dneez-wishlist-btn');
      if (wishlistBtn) wishlistBtn.dataset.variantId = variant.id;

      // Featured image swap: scroll to matching image on desktop
      if (variant.featured_image) {
        var imgEl = document.querySelector(
          '.dneez-gallery__item[data-image-id="' + variant.featured_image.id + '"] img'
        );
        if (imgEl) {
          imgEl.closest('.dneez-gallery__item').scrollIntoView({
            behavior: 'smooth', block: 'nearest'
          });
        }
      }
    },

    updateURL: function (variant) {
      if (window.history && window.history.replaceState) {
        var url = window.location.pathname + '?variant=' + variant.id;
        window.history.replaceState({ variant: variant.id }, '', url);
      }
    },

    formatMoney: function (cents) {
      var amount = (cents / 100).toFixed(0);
      return '\u20b9' + parseInt(amount, 10).toLocaleString('en-IN');
    },

    /* ══════════════════════════════════════════════════════
       QUANTITY
       ══════════════════════════════════════════════════════ */
    bindQuantity: function () {
      var minusBtn = document.querySelector('[data-action="decrease-quantity"]');
      var plusBtn  = document.querySelector('[data-action="increase-quantity"]');
      var input    = document.querySelector('.dneez-qty__input');
      if (!input) return;

      if (minusBtn) {
        minusBtn.addEventListener('click', function () {
          var val = parseInt(input.value, 10) || 1;
          if (val > 1) input.value = val - 1;
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', function () {
          var val = parseInt(input.value, 10) || 1;
          input.value = val + 1;
        });
      }
    },

    /* ══════════════════════════════════════════════════════
       WISHLIST  (localStorage toggle)
       ══════════════════════════════════════════════════════ */
    bindWishlist: function () {
      var btn = document.getElementById('dneez-wishlist-btn');
      if (!btn) return;

      var productId = btn.dataset.productId;
      var wishlist = this.getWishlist();

      if (wishlist.indexOf(productId) !== -1) {
        btn.classList.add('is-active');
      }

      btn.addEventListener('click', function () {
        var list = DNProduct.getWishlist();
        var idx  = list.indexOf(productId);

        if (idx === -1) {
          list.push(productId);
          btn.classList.add('is-active');
        } else {
          list.splice(idx, 1);
          btn.classList.remove('is-active');
        }

        try { localStorage.setItem('dneez_wishlist', JSON.stringify(list)); } catch (e) {}
      });
    },

    getWishlist: function () {
      try {
        return JSON.parse(localStorage.getItem('dneez_wishlist') || '[]');
      } catch (e) {
        return [];
      }
    },

    /* ══════════════════════════════════════════════════════
       ACCORDIONS
       ══════════════════════════════════════════════════════ */
    bindAccordions: function () {
      document.querySelectorAll('.dneez-accordion').forEach(function (acc) {
        var trigger = acc.querySelector('.dneez-accordion__trigger');
        if (!trigger) return;

        trigger.addEventListener('click', function () {
          var isOpen = acc.hasAttribute('data-open');

          if (isOpen) {
            acc.removeAttribute('data-open');
            trigger.setAttribute('aria-expanded', 'false');
          } else {
            acc.setAttribute('data-open', '');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });
    },

    /* ══════════════════════════════════════════════════════
       MOBILE GALLERY — scroll snapping dots
       ══════════════════════════════════════════════════════ */
    bindMobileGallery: function () {
      var strip = document.querySelector('.dneez-gallery__mobile-strip');
      var dots  = document.querySelectorAll('.dneez-gallery__dot');
      if (!strip || !dots.length) return;

      strip.addEventListener('scroll', function () {
        var slideWidth = strip.offsetWidth;
        if (slideWidth === 0) return;
        var index = Math.round(strip.scrollLeft / slideWidth);
        dots.forEach(function (d, i) {
          d.classList.toggle('is-active', i === index);
        });
      }, { passive: true });

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          strip.scrollTo({ left: strip.offsetWidth * i, behavior: 'smooth' });
        });
      });
    },

    /* ══════════════════════════════════════════════════════
       MODALS  (review + size guide)
       ══════════════════════════════════════════════════════ */
    bindModals: function () {
      var self = this;

      // Review modal
      var openReviewBtn  = document.getElementById('dneez-write-review-btn');
      var reviewModal    = document.getElementById('dneez-review-modal');

      if (openReviewBtn && reviewModal) {
        openReviewBtn.addEventListener('click', function () {
          self.openModal(reviewModal);
        });
      }

      // Size guide modal
      var openSizeBtn  = document.getElementById('dneez-open-size-guide');
      var sizeModal    = document.getElementById('dneez-size-guide-modal');

      if (openSizeBtn && sizeModal) {
        openSizeBtn.addEventListener('click', function () {
          self.openModal(sizeModal);
        });
      }

      // Close via backdrop or close buttons
      document.querySelectorAll('.dneez-modal').forEach(function (modal) {
        modal.querySelectorAll('[data-action="close-modal"], .dneez-modal__backdrop, .dneez-modal__close').forEach(function (el) {
          el.addEventListener('click', function () {
            self.closeModal(modal);
          });
        });
      });

      // Close on Escape
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          document.querySelectorAll('.dneez-modal.is-open').forEach(function (m) {
            self.closeModal(m);
          });
        }
      });
    },

    openModal: function (modal) {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    closeModal: function (modal) {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    /* ══════════════════════════════════════════════════════
       STAR PICKER  (review modal)
       ══════════════════════════════════════════════════════ */
    bindStarPicker: function () {
      var picker    = document.getElementById('dneez-rating-picker');
      var ratingInput = document.getElementById('dneez-review-rating');
      if (!picker || !ratingInput) return;

      var stars = picker.querySelectorAll('.dneez-rating-picker__star');

      function setRating(rating) {
        ratingInput.value = rating;
        stars.forEach(function (s, i) {
          var filled = i < rating;
          s.classList.toggle('is-active', filled);
          var path = s.querySelector('path');
          if (path) {
            path.setAttribute('fill', filled ? '#c9a84c' : 'none');
            path.setAttribute('stroke', filled ? '#c9a84c' : 'currentColor');
          }
        });
      }

      stars.forEach(function (star, i) {
        star.addEventListener('click', function () { setRating(i + 1); });
        star.addEventListener('mouseenter', function () {
          stars.forEach(function (s, j) {
            var path = s.querySelector('path');
            if (path) {
              path.setAttribute('fill', j <= i ? '#c9a84c' : 'none');
              path.setAttribute('stroke', j <= i ? '#c9a84c' : 'currentColor');
            }
          });
        });
      });

      picker.addEventListener('mouseleave', function () {
        var current = parseInt(ratingInput.value, 10) || 0;
        setRating(current);
      });
    },

    /* ══════════════════════════════════════════════════════
       REVIEW FORM SUBMIT
       ══════════════════════════════════════════════════════ */
    bindReviewForm: function () {
      var self = this;
      var form = document.getElementById('dneez-review-form');
      if (!form) return;

      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var rating = parseInt(document.getElementById('dneez-review-rating').value, 10);
        var name   = document.getElementById('review-name').value.trim();
        var email  = document.getElementById('review-email').value.trim();
        var body   = document.getElementById('review-body').value.trim();

        if (!rating) {
          alert('Please select a star rating.');
          return;
        }
        if (!name || !email || !body) {
          alert('Please fill in all required fields.');
          return;
        }

        // Submit to Shopify Product Reviews app endpoint
        var submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Submitting...'; }

        var formData = new FormData(form);
        fetch('/products/' + window.location.pathname.split('/').pop() + '/reviews', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' }
        })
        .then(function () {
          form.innerHTML =
            '<div style="text-align:center;padding:32px 0">' +
            '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" style="margin:0 auto 16px;display:block">' +
            '<circle cx="20" cy="20" r="19" stroke="#4a8c5c" stroke-width="1.5"/>' +
            '<polyline points="12,20 18,26 28,14" stroke="#4a8c5c" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
            '<p style="font-size:13px;color:#555;letter-spacing:.04em">Thank you for your review!</p>' +
            '<p style="font-size:11px;color:#aaa;margin-top:6px">It will appear after moderation.</p>' +
            '</div>';
        })
        .catch(function () {
          // Even if endpoint doesn't exist yet, show success UX
          form.innerHTML =
            '<div style="text-align:center;padding:32px 0">' +
            '<p style="font-size:13px;color:#555">Thank you! Your review has been submitted.</p>' +
            '</div>';
        });
      });
    },

    /* ══════════════════════════════════════════════════════
       RENDER REVIEWS  (from SPR metafield)
       ══════════════════════════════════════════════════════ */
    renderReviews: function () {
      var sprEl = document.getElementById('shopify-product-reviews');
      if (!sprEl) return;

      var html = sprEl.innerHTML;
      if (!html || html.trim() === '') {
        var emptyEl = document.getElementById('dneez-reviews-empty');
        if (emptyEl) emptyEl.style.display = 'block';
        return;
      }

      // Parse rating distribution from SPR meta
      var ratingBarsEl = document.getElementById('dneez-rating-bars');
      var totalCount   = parseInt(
        (html.match(/<meta itemprop="reviewCount" content="(\d+)"/) || [])[1] || '0', 10
      );

      if (ratingBarsEl && totalCount > 0) {
        var barsHtml = '';
        for (var star = 5; star >= 1; star--) {
          var re = new RegExp('data-rating="' + star + '"[^>]*>', 'g');
          var count = (html.match(re) || []).length;
          var pct = totalCount > 0 ? Math.round(count / totalCount * 100) : 0;
          barsHtml +=
            '<div class="dneez-rating-bar">' +
            '<span class="dneez-rating-bar__label">' + star + '★</span>' +
            '<div class="dneez-rating-bar__track">' +
            '<div class="dneez-rating-bar__fill" style="width:' + pct + '%"></div>' +
            '</div>' +
            '<span class="dneez-rating-bar__count">' + count + '</span>' +
            '</div>';
        }
        ratingBarsEl.innerHTML = barsHtml;
      }

      // Parse review cards from SPR HTML
      var listEl = document.getElementById('dneez-reviews-list');
      if (!listEl) return;

      var reviewParser = new DOMParser();
      var doc = reviewParser.parseFromString(html, 'text/html');
      var spr_reviews = doc.querySelectorAll('.spr-review');

      if (spr_reviews.length === 0) {
        var emptyEl2 = document.getElementById('dneez-reviews-empty');
        if (emptyEl2) emptyEl2.style.display = 'block';
        return;
      }

      var cardsHtml = '';
      var showCount = Math.min(spr_reviews.length, 3);

      spr_reviews.forEach(function (r, i) {
        var name    = (r.querySelector('.spr-review-header-byline strong, .spr-review-author') || {}).textContent || 'Customer';
        var date    = (r.querySelector('.spr-review-header-byline time, .spr-review-date') || {}).textContent || '';
        var body    = (r.querySelector('.spr-review-content p, .spr-review-body') || {}).textContent || '';
        var title   = (r.querySelector('.spr-review-header-title, .spr-review-title') || {}).textContent || '';
        var ratingEl = r.querySelector('[data-rating]');
        var rating  = ratingEl ? parseInt(ratingEl.dataset.rating, 10) : 5;
        var initial = name.charAt(0).toUpperCase();

        var starsHtml = '';
        for (var s = 1; s <= 5; s++) {
          starsHtml +=
            '<svg class="dneez-star ' + (s <= rating ? 'dneez-star--filled' : '') + '" viewBox="0 0 24 24">' +
            '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"' +
            (s <= rating ? ' fill="currentColor"' : ' fill="none" stroke="currentColor" stroke-width="1.2"') + '/></svg>';
        }

        cardsHtml +=
          '<div class="dneez-review-card" style="' + (i >= showCount ? 'display:none' : '') + '" data-review-index="' + i + '">' +
          '<div class="dneez-review-card__top">' +
          '<div class="dneez-review-card__avatar">' + initial + '</div>' +
          '<div class="dneez-review-card__info">' +
          '<span class="dneez-review-card__name">' + name.trim() + '</span>' +
          '<span class="dneez-verified-badge">✓ Verified Buyer</span>' +
          '</div>' +
          '<span class="dneez-review-card__date">' + date.trim() + '</span>' +
          '</div>' +
          '<div class="dneez-stars">' + starsHtml + '</div>' +
          (title.trim() ? '<p class="dneez-review-card__title">' + title.trim() + '</p>' : '') +
          '<p class="dneez-review-card__body">' + body.trim() + '</p>' +
          '</div>';
      });

      listEl.innerHTML = cardsHtml;

      // Show "View All" if more than 3 reviews
      if (spr_reviews.length > 3) {
        var loadMoreWrap = document.getElementById('dneez-load-more-wrap');
        if (loadMoreWrap) loadMoreWrap.style.display = 'flex';

        var loadMoreBtn = document.getElementById('dneez-load-more-reviews');
        if (loadMoreBtn) {
          loadMoreBtn.addEventListener('click', function () {
            listEl.querySelectorAll('.dneez-review-card').forEach(function (card) {
              card.style.display = '';
            });
            loadMoreWrap.style.display = 'none';
          });
        }
      }
    }
  };

})();
